import React, { useState, useEffect } from 'react';
import {
  AccountIcon,
  BusinessIcon,
  CalendarIcon,
  CancelIcon,
  CheckIcon,
  CloseIcon,
  CommentIcon,
  DeleteIcon,
  EditIcon,
  EmailIcon,
  InventoryIcon,
  PhoneIcon,
  SaveIcon,
  SendIcon,
  UncheckedIcon,
} from '../icons';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  adminAddAppUserComment,
  adminDeleteAppUserComment,
  adminGetAppUser,
  adminUpdateAppUser,
  adminUpdateAppUserComment,
  toUserDate,
} from '../../api/appUsersApi';

const InfoRow = ({ icon, label, value }) => (
  <div className="admin-row" style={{ marginBottom: 16 }}>
    {icon}
    <div>
      <div className="admin-label">{label}</div>
      <div>{value || 'N/A'}</div>
    </div>
  </div>
);

const UserProfileDialog = ({ open, onClose, user }) => {
  const { admin: currentAdmin } = useAdminAuth();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [isDeleting, setIsDeleting] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [markingReachedOut, setMarkingReachedOut] = useState(false);
  const [userData, setUserData] = useState(user);

  // Admin profile comes from the admin Postgres session, not Firestore.
  useEffect(() => {
    if (!currentAdmin?.uid) return;
    setAdminData(currentAdmin);
  }, [currentAdmin]);

  // Keep the dialog in sync with Postgres while it is open.
  useEffect(() => {
    if (!open || !user?.id) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const fetched = await adminGetAppUser(user.id);
        if (!cancelled && fetched) {
          setUserData(fetched);
          setComments(fetched.comments || []);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    load();
    const timer = setInterval(load, 12000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, user?.id]);

  useEffect(() => {
    if (open) setUserData(user);
  }, [open, user]);

  if (!userData) return null;

  const getAdminDisplayName = () => {
    // Use adminData if available, otherwise fall back to currentAdmin
    const admin = adminData || currentAdmin;
    
    if (!admin) return 'Admin';
    
    const firstName = admin.firstName || '';
    const lastName = admin.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return admin.email || 'Admin';
  };

  const formatDateTime = (timestamp) => {
    const date = toUserDate(timestamp);
    if (!date) return 'N/A';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const applyUser = (updated) => {
    if (!updated) return;
    setUserData(updated);
    setComments(updated.comments || []);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const updated = await adminAddAppUserComment(userData.id, newComment.trim());
      applyUser(updated);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    setIsDeleting(comment.id);
    try {
      const updated = await adminDeleteAppUserComment(userData.id, comment.id);
      applyUser(updated);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditText(comment.text);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditText('');
  };

  const handleUpdateComment = async () => {
    if (!editText.trim()) return;
    
    setIsUpdating(true);
    try {
      const updated = await adminUpdateAppUserComment(userData.id, editingComment, editText.trim());
      applyUser(updated);
      setEditingComment(null);
      setEditText('');
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete) return;
    
    setIsDeleting(commentToDelete.id);
    try {
      const updated = await adminDeleteAppUserComment(userData.id, commentToDelete.id);
      applyUser(updated);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsDeleting(null);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const handleMarkReachedOut = async () => {
    if (!userData?.id) return;
    
    setMarkingReachedOut(true);
    try {
      const updated = await adminUpdateAppUser(userData.id, { reachedOut: true });
      applyUser(updated);
    } catch (error) {
      console.error('Error marking user as reached out:', error);
    } finally {
      setMarkingReachedOut(false);
    }
  };

  const canEditComment = (comment) => {
    const currentAdminName = getAdminDisplayName();
    return comment.createdBy === currentAdminName;
  };

  if (!open || !userData) return null;

  return (
    <>
      <button type="button" className="admin-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal-header">
        <div className="admin-row">
          <AccountIcon />
          <h2 className="admin-heading-sm">User Profile</h2>
        </div>
        <button type="button" className="admin-icon-btn" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>
      <div className="admin-modal-body">
        <div>
            <div className="admin-panel-box">
              <h3 className="admin-heading-sm" style={{ marginBottom: 8 }}>
                {userData.firstName} {userData.lastName}
              </h3>
              <span className={`badge ${userData.status === 'active' ? 'badge-active' : 'badge-default'}`}>
                {userData.status || 'active'}
              </span>
              {' '}
              <span className="badge badge-default">{userData.package?.name || 'N/A'}</span>
            </div>

          <div>
            <InfoRow
              icon={<BusinessIcon className="admin-icon-accent" />}
              label="Company"
              value={userData.companyName}
            />
            <InfoRow
              icon={<EmailIcon className="admin-icon-accent" />}
              label="Email"
              value={userData.email}
            />
            <InfoRow
              icon={<PhoneIcon className="admin-icon-accent" />}
              label="Business Phone"
              value={userData.businessPhoneFormatted || userData.businessPhone || 'N/A'}
            />
            {(userData.personalPhoneFormatted || userData.personalPhone) && (
              <InfoRow
                icon={<PhoneIcon className="admin-icon-muted" />}
                label="Personal Phone"
                value={userData.personalPhoneFormatted || userData.personalPhone}
              />
            )}
          </div>

          <div>
            <InfoRow
              icon={<CalendarIcon className="admin-icon-accent" />}
              label="Signup Date"
              value={toUserDate(userData.createdAt)?.toLocaleDateString() || 'N/A'}
            />
            <InfoRow
              icon={<InventoryIcon className="admin-icon-accent" />}
              label="Subscription Status"
              value={
                <span className={`badge ${userData.package?.subscription?.status === 'active' ? 'badge-active' : 'badge-warning'}`}>
                  {userData.package?.subscription?.status || 'N/A'}
                </span>
              }
            />
            <div className="admin-row" style={{ marginBottom: 16 }}>
              {userData.reachedOut ? 
                <CheckIcon className="admin-icon-success" /> : 
                <button
                  type="button"
                  className="admin-icon-btn"
                  onClick={handleMarkReachedOut}
                  disabled={markingReachedOut}
                >
                  {markingReachedOut ? '…' : <UncheckedIcon />}
                </button>
              }
              <div>
                <div className="admin-label">
                  Reach Out Status
                </div>
                {userData.reachedOut ? (
                  <div>
                    <p style={{ margin: 0, color: '#16a34a' }}>
                      Reached Out
                    </p>
                    <p className="admin-label">
                      by {userData.reachedOutBy} on {toUserDate(userData.reachedOutAt)?.toLocaleDateString() || ''}
                    </p>
                  </div>
                ) : (
                  <p className="admin-error-text">
                    Not Reached Out
                  </p>
                )}
              </div>
            </div>
          </div>

          {userData.package && (
            <div>
              <hr className="admin-divider" />
              <h3 className="admin-heading-sm" style={{ marginBottom: 16, fontSize: '0.95rem' }}>
                Package Details
              </h3>
              <div className="admin-panel-box">
                <div className="admin-grid-2">
                  <div>
                    <div className="admin-label">
                      Billing Cycle
                    </div>
                    <div>
                      {userData.package.subscription?.billingCycle || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="admin-label">
                      Next Billing
                    </div>
                    <div>
                      {toUserDate(userData.package.subscription?.nextBillingDate)?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <hr className="admin-divider" />
            <div className="admin-row" style={{ marginBottom: 16 }}>
              <CommentIcon className="admin-icon-accent" />
              <h3 className="admin-heading-sm" style={{ fontSize: '0.95rem' }}>
                Comments & Feedback
              </h3>
            </div>

            <div style={{ marginBottom: 24 }}>
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className="admin-panel-box" style={{ marginBottom: 8 }}>
                    <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      {editingComment === comment.id ? (
                        <textarea
                          className="admin-field"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={2}
                          disabled={isUpdating}
                          style={{ flex: 1, marginRight: 8 }}
                        />
                      ) : (
                        <div style={{ flex: 1, fontSize: '0.875rem' }}>
                          {comment.text}
                        </div>
                      )}
                      <div className="admin-row">
                        {canEditComment(comment) && editingComment === comment.id ? (
                          <>
                            <button
                              type="button"
                              className="admin-icon-btn"
                              onClick={handleUpdateComment}
                              disabled={isUpdating}
                              aria-label="Save"
                            >
                              {isUpdating ? '…' : <SaveIcon />}
                            </button>
                            <button
                              type="button"
                              className="admin-icon-btn"
                              onClick={handleCancelEdit}
                              aria-label="Cancel"
                            >
                              <CancelIcon />
                            </button>
                          </>
                        ) : canEditComment(comment) ? (
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => handleEditComment(comment)}
                            aria-label="Edit"
                          >
                            <EditIcon />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="admin-icon-btn"
                          onClick={() => handleDeleteClick(comment)}
                          disabled={isDeleting === comment.id}
                          aria-label="Delete"
                        >
                          {isDeleting === comment.id ? '…' : <DeleteIcon />}
                        </button>
                      </div>
                    </div>
                    <div className="admin-row" style={{ marginTop: 8 }}>
                      <div className="admin-label">
                        by {comment.createdBy}
                      </div>
                      <div className="admin-label">
                        • {formatDateTime(comment.createdAt)}
                      </div>
                    </div>
                    {comment.updatedAt && (
                      <div className="admin-label" style={{ marginTop: 4 }}>
                        (edited by {comment.updatedBy || comment.createdBy} • {formatDateTime(comment.updatedAt)})
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="admin-muted" style={{ marginBottom: 16 }}>
                  No comments yet
                </p>
              )}
            </div>

            <div className="admin-row">
              <textarea
                className="admin-field"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={2}
                disabled={isSubmitting}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={handleAddComment}
                disabled={!newComment.trim() || isSubmitting}
              >
                Add <SendIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {deleteDialogOpen && (
        <>
          <button type="button" className="admin-modal-backdrop" aria-label="Close" onClick={handleDeleteCancel} />
          <div className="admin-modal admin-modal--sm" role="dialog" aria-modal="true">
            <div className="admin-modal-header">
              <div className="admin-row">
                <DeleteIcon className="admin-icon-danger" />
                <h2 className="admin-heading-sm">Delete Comment</h2>
              </div>
              <button type="button" className="admin-icon-btn" onClick={handleDeleteCancel} aria-label="Close">
                <CloseIcon />
              </button>
            </div>
            <div className="admin-modal-body">
              <p style={{ margin: '0 0 16px' }}>
                Are you sure you want to delete this comment?
              </p>
              {commentToDelete && (
                <div className="admin-panel-box" style={{ marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontStyle: 'italic' }}>
                    "{commentToDelete.text}"
                  </p>
                  <p className="admin-label" style={{ marginTop: 8 }}>
                    by {commentToDelete.createdBy} • {formatDateTime(commentToDelete.createdAt)}
                  </p>
                </div>
              )}
              <p className="admin-muted">
                This action cannot be undone.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-btn"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-danger"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Comment'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UserProfileDialog; 